/**
 * Navigation Configuration
 *
 * Defines all navigation items and their permission requirements.
 * Items are displayed based on the user's permission level.
 */

import type React from "react";
import type { Permission } from "./permissions";
import { EXTERNAL_URLS } from "./external-urls";
import { UploadIcon } from "~/components/icons";
import { ExternalLinkIcon } from "~/components/icons";

export interface NavigationItem {
	id: string;
	label: string;
	href: string;
	icon: string;
	minPermission: Permission | "all";
	hasSubmenu?: boolean;
	subItems?: NavigationSubItem[];
}

export interface NavigationSubItem {
	id: string;
	label: string;
	href: string;
	minPermission: Permission | "all";
	isExternal?: boolean;
	isDownload?: boolean;
	icon?: React.ComponentType<{ className?: string }>;
}

export const ADMIN_NAV_ITEMS: NavigationItem[] = [
	{
		id: "dashboard",
		label: "Dashboard",
		href: "/",
		icon: "dashboard",
		minPermission: "a",
	},
	{
		id: "organizations",
		label: "Organizations",
		href: "/organizations",
		icon: "building",
		minPermission: "a",
	},
	{
		id: "jurisdictions",
		label: "Jurisdictions",
		href: "/jurisdictions",
		icon: "compass",
		minPermission: "a",
	},
	{
		id: "accounts",
		label: "Accounts",
		href: "/accounts",
		icon: "users",
		minPermission: "a",
	},
	{
		id: "actions",
		label: "Actions",
		href: "/actions",
		icon: "zap",
		minPermission: "a",
	},
	{
		id: "contacts",
		label: "Contacts",
		href: "/contacts",
		icon: "message-circle",
		minPermission: "a",
	},
];

export const RW_NAV_ITEMS: NavigationItem[] = [
	{
		id: "dashboard",
		label: "Dashboard",
		href: "/",
		icon: "dashboard",
		minPermission: "r",
	},
	{
		id: "search",
		label: "Search",
		href: "/search",
		icon: "search",
		minPermission: "r",
		hasSubmenu: true,
		subItems: [
			{
				id: "search-actions",
				label: "Search Actions",
				href: "/search/actions",
				minPermission: "r",
			},
			{
				id: "search-bulk",
				label: "Bulk Search with File",
				href: "/search/bulk",
				minPermission: "r",
			},
			{
				id: "search-history",
				label: "Search History",
				href: "/search/history",
				minPermission: "r",
			},
			{
				id: "search-license",
				label: "License Verification",
				href: EXTERNAL_URLS.ARELLO.PROGRAMS.LICENSE_VERIFICATION,
				minPermission: "r",
				isExternal: true,
				icon: ExternalLinkIcon,
			},
		],
	},
	{
		id: "submissions",
		label: "Submissions",
		href: "/submissions",
		icon: "edit",
		minPermission: "w",
		hasSubmenu: true,
		subItems: [
			{
				id: "submissions-manage",
				label: "Manage Actions",
				href: "/submissions/manage",
				minPermission: "w",
			},
			{
				id: "submissions-add",
				label: "Add Action",
				href: "/submissions/add",
				minPermission: "w",
			},
			{
				id: "submissions-bulk",
				label: "Bulk Add with File",
				href: "/submissions/bulk",
				minPermission: "w",
			},
		],
	},
	{
		id: "organizations",
		label: "Organizations",
		href: "/organizations",
		icon: "building",
		minPermission: "r",
		hasSubmenu: true,
		subItems: [
			{
				id: "org-settings",
				label: "Settings",
				href: "/organizations/settings",
				minPermission: "r",
			},
			{
				id: "org-invoices",
				label: "Invoices",
				href: "/organizations/invoices",
				minPermission: "r",
			},
		],
	},
	{
		id: "general-info",
		label: "General Information",
		href: "/general-information",
		icon: "info-circle",
		minPermission: "r",
		hasSubmenu: true,
		subItems: [
			{
				id: "info-statistics",
				label: "Database Statistics",
				href: "/general-information/statistics",
				minPermission: "r",
			},
			{
				id: "info-terms",
				label: "Terms of Use",
				href: "/general-information/terms",
				minPermission: "r",
			},
			{
				id: "info-policy",
				label: "Policy and Procedures",
				href: "/general-information/policy",
				minPermission: "r",
			},
			{
				id: "info-guide",
				label: "Download User's Guide",
				href: "/DADB_UsersGuide2022.pdf",
				minPermission: "r",
				isDownload: true,
			},
		],
	},
];

export const USER_SECTION_ITEMS = [
	{
		id: "help",
		label: "Help Center",
		href: "/help",
		icon: "help-circle",
	},
	{
		id: "logout",
		label: "Log Out",
		href: "/api/auth/signout",
		icon: "log-out",
	},
] as const;

export function getNavigationItems(
	permission: string | null | undefined,
): NavigationItem[] {
	if (permission === "a") {
		return ADMIN_NAV_ITEMS;
	}

	return RW_NAV_ITEMS.filter((item) => {
		if (item.minPermission === "all") return true;

		if (permission === "w" && item.id === "search") {
			return false;
		}

		if (permission === "rw") {
			return ["r", "w", "rw"].includes(item.minPermission);
		}
		if (permission === "w") {
			return ["r", "w"].includes(item.minPermission);
		}
		if (permission === "r") {
			return item.minPermission === "r";
		}

		return false;
	});
}
